import React, { useState, useEffect } from 'react';
import { Button, Badge, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { 
  getUserOffers, 
  userUpdateOffer, 
  userDeleteOffer,
  userPauseOffer,
  type Offer 
} from '../../services/p2pAPI';
import GenericButton from '../updated/shared/Button';
import './OfferManagement.css';
import './TradesManagement.css';

const MyOffers: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'paused' | 'deleted'>('active');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const [editFormData, setEditFormData] = useState({
    price: '',
    amount: '',
    minAmount: '',
    maxAmount: '',
    terms: '',
    autoReply: '',
    status: 'active' as 'active' | 'paused' | 'deleted',
  });
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pausing, setPausing] = useState<string | null>(null);

  useEffect(() => {
    loadOffers();
  }, [activeTab]);

  // Also reload when component mounts to catch any new offers
  useEffect(() => {
    const interval = setInterval(() => {
      loadOffers();
    }, 5000); // Refresh every 5 seconds
    
    return () => clearInterval(interval);
  }, [activeTab]);

  const loadOffers = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Loading offers for tab:', activeTab);
      const response = await getUserOffers({
        status: activeTab,
        limit: 100,
        offset: 0
      });
      
      console.log('getUserOffers response:', response);
      console.log('Offers data:', response.data);
      console.log('Number of offers:', response.data?.length || 0);
      
      const offersList = response.data || [];
      setOffers(offersList);
      
      if (offersList.length === 0) {
        console.log('No offers found for status:', activeTab);
      }
    } catch (error: any) {
      console.error('Failed to load offers:', error);
      const errorMessage = error?.message || 
                          error?.response?.data?.message || 
                          error?.response?.data?.error ||
                          error?.error ||
                          'Failed to load offers';
      setError(errorMessage);
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (offer: Offer) => {
    setSelectedOffer(offer);
    setEditFormData({
      price: offer.price.toString(),
      amount: offer.amount.toString(),
      minAmount: offer.minAmount.toString(),
      maxAmount: offer.maxAmount.toString(),
      terms: offer.terms || '',
      autoReply: offer.autoReply || '',
      status: offer.status || 'active',
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedOffer) return;

    try {
      setUpdating(true);
      setError('');

      const updateData: any = {};
      if (editFormData.price) updateData.price = parseFloat(editFormData.price);
      if (editFormData.amount) updateData.amount = parseFloat(editFormData.amount);
      if (editFormData.minAmount) updateData.minAmount = parseFloat(editFormData.minAmount);
      if (editFormData.maxAmount) updateData.maxAmount = parseFloat(editFormData.maxAmount);
      if (editFormData.terms !== undefined) updateData.terms = editFormData.terms;
      if (editFormData.autoReply !== undefined) updateData.autoReply = editFormData.autoReply;
      if (editFormData.status) updateData.status = editFormData.status;

      await userUpdateOffer(selectedOffer.id, updateData);
      setShowEditModal(false);
      await loadOffers();
    } catch (error: any) {
      console.error('Failed to update offer:', error);
      setError(error?.response?.data?.message || error?.message || 'Failed to update offer');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedOffer) return;

    try {
      setDeleting(true);
      setError('');

      // Use DELETE endpoint - backend should mark it as deleted (soft delete)
      await userDeleteOffer(selectedOffer.id);
      setShowDeleteModal(false);
      
      // Switch to deleted tab to show the deleted offer
      // The useEffect will automatically reload offers when activeTab changes
      if (activeTab !== 'deleted') {
        setActiveTab('deleted');
      } else {
        // If already on deleted tab, reload to refresh the list
        await loadOffers();
      }
    } catch (error: any) {
      console.error('Failed to delete offer:', error);
      setError(error?.response?.data?.message || error?.message || 'Failed to delete offer');
    } finally {
      setDeleting(false);
    }
  };

  const handlePauseResume = async (offer: Offer) => {
    try {
      setPausing(offer.id);
      setError('');

      await userPauseOffer(offer.id);
      await loadOffers();
    } catch (error: any) {
      console.error('Failed to pause/resume offer:', error);
      setError(error?.response?.data?.message || error?.message || 'Failed to pause/resume offer');
    } finally {
      setPausing(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { variant: 'success', text: 'Active' },
      paused: { variant: 'warning', text: 'Paused' },
      deleted: { variant: 'danger', text: 'Deleted' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { 
      variant: 'secondary', 
      text: status 
    };

    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to get token logo
  const getTokenLogo = (coinSymbol: string) => {
    try {
      const normalizedSymbol = coinSymbol.toUpperCase().trim();
      const symbolMap: { [key: string]: string } = {
        'USD': 'USD', 'USDT': 'USDT', 'USDC': 'USDC', 'BTC': 'BTC', 'ETH': 'ETH',
        'BNB': 'BNB', 'BUSD': 'BUSD', 'INEX': 'INEX', 'IN500': 'IN500', 'INXC': 'INXC',
        'IUSD': 'IUSD', 'IUSD+': 'IUSD+', 'IUSDP': 'IUSDP', 'ADA': 'ADA', 'DOT': 'DOT',
        'LINK': 'LINK', 'MATIC': 'MATIC', 'SOL': 'SOL', 'XRP': 'XRP', 'DOGE': 'DOGE',
        'AVAX': 'AVAX', 'LTC': 'LTC', 'CAKE': 'CAKE', 'AAVE': 'AAVE', 'SHIB': 'SHIB',
        'TRX': 'TRX', 'VET': 'VET', 'CHZ': 'CHZ', 'MANA': 'MANA', 'THETA': 'THETA',
        'FTM': 'FTM', 'ALGO': 'ALCRYP', 'BCH': 'BCH', 'XLM': 'XLM', 'TUSD': 'TUSD',
        'DAI': 'DAI', 'IBAT': 'IBAT', 'EPS': 'EPS', 'LBLOCK': 'LBlock',
      };
      let imageName = symbolMap[normalizedSymbol] || normalizedSymbol;
      if (normalizedSymbol.includes('+') || normalizedSymbol.includes('-')) {
        imageName = normalizedSymbol.replace(/[+-\s]/g, '');
      }
      try {
        return require(`../../assets/token-icons/${imageName}.png`).default;
      } catch (error1) {
        try {
          return require(`../../assets/token-icons/${normalizedSymbol}.png`).default;
        } catch (error2) {
          return null;
        }
      }
    } catch (error) {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="trades-loading-state">
        <Spinner animation="border" role="status" />
        <p>Loading offers...</p>
      </div>
    );
  }

  return (
    <div className="my-offers-container">
      {error && (
        <div className="error-alert">
          <Alert variant="danger">
            <Alert.Heading>Error</Alert.Heading>
            <p>{error}</p>
            <hr />
            <div className="d-flex justify-content-end">
              <Button variant="outline-danger" onClick={loadOffers}>
                <i className="fas fa-sync-alt me-2"></i>
                Retry
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="trade-filter-section">
        <div className="filter-buttons-container">
          <button
            className={`filter-button ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            <i className="fas fa-check-circle me-2"></i>
            Active
          </button>
          <button
            className={`filter-button ${activeTab === 'paused' ? 'active' : ''}`}
            onClick={() => setActiveTab('paused')}
          >
            <i className="fas fa-pause-circle me-2"></i>
            Paused
          </button>
          <button
            className={`filter-button ${activeTab === 'deleted' ? 'active' : ''}`}
            onClick={() => setActiveTab('deleted')}
          >
            <i className="fas fa-trash me-2"></i>
            Deleted
          </button>
        </div>
      </div>

      {/* Offers List */}
      {offers.length === 0 ? (
        <div className="empty-state-modern">
          <div className="empty-state-icon">
            <i className="fas fa-list"></i>
          </div>
          <h4>No Offers Found</h4>
          <p className="empty-state-text">
            {activeTab === 'active' 
              ? "You don't have any active offers. Create a new offer to get started."
              : `You don't have any ${activeTab} offers.`}
          </p>
        </div>
      ) : (
        <div className="binance-results-section">
          <div className="results-header">
            <div className="results-info">
              <span className="results-type">
                {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Offers ({offers.length})
              </span>
            </div>
          </div>

          <div className="binance-trades-table">
            <div className={`trades-table-header ${activeTab === 'deleted' ? 'no-actions' : ''}`}>
              <div className="col-offer-id">Offer ID</div>
              <div className="col-type">Type</div>
              <div className="col-crypto">Crypto</div>
              <div className="col-amount">Available</div>
              <div className="col-price">Price</div>
              <div className="col-total">Total Value</div>
              <div className="col-status">Status</div>
              <div className="col-date">Created</div>
              {activeTab !== 'deleted' && <div className="col-action">Actions</div>}
            </div>

            <div className="trades-table-body">
              {offers.map((offer) => {
                const tokenLogo = getTokenLogo(offer.cryptoType);
                return (
                  <div key={offer.id} className={`trade-table-row ${activeTab === 'deleted' ? 'no-actions' : ''}`}>
                    <div className="col-offer-id">
                      <div className="offer-id-value">
                        <i className="fas fa-hashtag me-1"></i>
                        <code>{offer.id.slice(0, 10)}...</code>
                      </div>
                    </div>
                    <div className="col-type">
                      <span style={{ color: 'var(--body_color)', fontSize: '14px' }}>
                        {offer.type === 'buy' ? 'BUY' : 'SELL'}
                      </span>
                    </div>
                    <div className="col-crypto">
                      {tokenLogo ? (
                        <img 
                          src={tokenLogo} 
                          alt={offer.cryptoType}
                          className="crypto-icon-image"
                          style={{ width: '24px', height: '24px', borderRadius: '50%', marginRight: '8px' }}
                        />
                      ) : (
                        <Badge bg="info">{offer.cryptoType}</Badge>
                      )}
                    </div>
                    <div className="col-amount">
                      <div className="trade-amount-value">
                        {offer.amount.toFixed(4)} {offer.cryptoType}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--body_color)', opacity: 0.7, marginTop: '2px' }}>
                        Min: {offer.minAmount.toFixed(4)} | Max: {offer.maxAmount.toFixed(4)}
                      </div>
                    </div>
                    <div className="col-price">
                      <div className="trade-price-value">
                        {formatCurrency(offer.price)}
                      </div>
                    </div>
                    <div className="col-total">
                      <div className="trade-total-row-value">
                        {formatCurrency(offer.amount * offer.price)}
                      </div>
                    </div>
                    <div className="col-status">
                      <span style={{ color: 'var(--body_color)', fontSize: '14px' }}>
                        {offer.status === 'active' ? 'Active' : 
                         offer.status === 'paused' ? 'Paused' : 
                         offer.status === 'deleted' ? 'Deleted' : 
                         offer.status}
                      </span>
                    </div>
                    <div className="col-date">
                      <div className="trade-date-value">
                        {formatDate(offer.createdAt)}
                      </div>
                    </div>
                    {activeTab !== 'deleted' && (
                      <div className="col-action" onClick={(e) => e.stopPropagation()}>
                        <div className="trade-action-buttons">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="btn-tertiary-action"
                            onClick={() => handleEdit(offer)}
                          >
                            <i className="fas fa-edit"></i>
                            Edit
                          </Button>
                          <Button
                            variant={offer.status === 'paused' ? 'outline-success' : 'outline-warning'}
                            size="sm"
                            className="btn-tertiary-action"
                            onClick={() => handlePauseResume(offer)}
                            disabled={pausing === offer.id}
                          >
                            {pausing === offer.id ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-1" style={{ width: '10px', height: '10px' }} />
                                {offer.status === 'paused' ? 'Resuming...' : 'Pausing...'}
                              </>
                            ) : (
                              <>
                                <i className={`fas fa-${offer.status === 'paused' ? 'play' : 'pause'}`}></i>
                                {offer.status === 'paused' ? 'Resume' : 'Pause'}
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="btn-tertiary-action"
                            onClick={() => {
                              setSelectedOffer(offer);
                              setShowDeleteModal(true);
                            }}
                          >
                            <i className="fas fa-trash"></i>
                            Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Offer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Price (USD)</Form.Label>
              <Form.Control
                type="number"
                value={editFormData.price}
                onChange={(e) => setEditFormData({ ...editFormData, price: e.target.value })}
                step="0.01"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                value={editFormData.amount}
                onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })}
                step="0.01"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Min Amount</Form.Label>
              <Form.Control
                type="number"
                value={editFormData.minAmount}
                onChange={(e) => setEditFormData({ ...editFormData, minAmount: e.target.value })}
                step="0.01"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max Amount</Form.Label>
              <Form.Control
                type="number"
                value={editFormData.maxAmount}
                onChange={(e) => setEditFormData({ ...editFormData, maxAmount: e.target.value })}
                step="0.01"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={editFormData.status}
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as any })}
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="deleted">Deleted</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Terms</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editFormData.terms}
                onChange={(e) => setEditFormData({ ...editFormData, terms: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Auto Reply</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={editFormData.autoReply}
                onChange={(e) => setEditFormData({ ...editFormData, autoReply: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdate} disabled={updating}>
            {updating ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Update
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Offer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this offer? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MyOffers;

